import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../../authService';
import RPTDashboard from './RPTDashboard';
import PropertyOwners from './PropertyOwners';
import Properties from './Properties';
import Payments from './Payments';
import SettingsPage from './SettingsPage';
import AssessmentsPage from './pages/AssessmentsPage';
import ReportsPage from './pages/ReportsPage';
import TaxCalculator from './pages/TaxCalculator';
import CustomReportsPage from './pages/CustomReportsPage';
import ProfileModal from '../../modals/ProfileModal';
import SettingsModal from '../../modals/SettingsModal';
import MobileBottomBar from '../../common/MobileBottomBar';
import UnifiedSidebar from '../../common/UnifiedSidebar';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  useTheme,
  useMediaQuery,
  CssBaseline,
  Menu,
  MenuItem
} from '@mui/material';
import { SystemThemeProvider } from '../../../hooks/useSystemTheme'
import {
  Menu as MenuIcon,
  Dashboard,
  Apartment,
  People,
  Assessment,
  Payment,
  Calculate,
  Settings as SettingsIcon,
  ArrowBack,
  Logout,
  Person,
  BarChart
} from '@mui/icons-material';

const drawerWidth = 280;

const RPTSystem = ({ onLogout }) => {
  const [user, setUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const currentUser = authService.getUserSession();
    if (!currentUser) {
      navigate('/login');
    } else {
      setUser(currentUser);
    }
    document.title = 'Real Property Tax System - LGU Concepcion';
  }, [navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    authService.logout();
    if (onLogout) {
      onLogout();
    }
    navigate('/login', { replace: true });
  };

  const handleBackToPortal = () => {
    navigate('/portal');
  };

  const handleProfileClick = () => {
    handleMenuClose();
    setShowProfileModal(true);
  };

  const handleSettingsClick = () => {
    handleMenuClose();
    setShowSettingsModal(true);
  };

  const handleProfileUpdate = (updatedUser) => {
    setUser(updatedUser);
    authService.setUserSession(updatedUser);
  };

  const menuItems = [
    { name: 'Dashboard', icon: <Dashboard />, path: '/portal/property-tax/dashboard' },
    { name: 'Properties', icon: <Apartment />, path: '/portal/property-tax/properties' },
    { name: 'Payments', icon: <Payment />, path: '/portal/property-tax/payments' },
    { name: 'Calculator', icon: <Calculate />, path: '/portal/property-tax/calculator' },
    { name: 'Owners', icon: <People />, path: '/portal/property-tax/owners' },
    { name: 'Assessments', icon: <Assessment />, path: '/portal/property-tax/assessments' },
    { name: 'Reports', icon: <BarChart />, path: '/portal/property-tax/reports' },
    { name: 'Custom Reports', icon: <BarChart />, path: '/portal/property-tax/custom-reports' },
    { name: 'Settings', icon: <SettingsIcon />, path: '/portal/property-tax/settings' }
  ];

  const drawer = (
    <UnifiedSidebar
      user={user}
      onLogout={handleLogout}
      onCloseMobile={() => setMobileOpen(false)}
      onOpenProfile={handleProfileClick}
      onOpenSettings={handleSettingsClick}
    />
  );

  if (!user) return null;

  return (
    <SystemThemeProvider systemId="property-tax">
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        
        {/* Mobile Top AppBar */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${drawerWidth}px` },
            display: { md: 'none' },
            bgcolor: '#ffffff',
            color: '#0f172a',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Toolbar sx={{ minHeight: 56 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1.5, color: '#0f172a' }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Apartment sx={{ color: '#059669', fontSize: 22 }} />
              <Typography variant="subtitle1" fontWeight={800} color="#0f172a" noWrap>
                Real Property Tax System
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Side Navigation Drawers */}
        <Box
          component="nav"
          sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid #e2e8f0' },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid #e2e8f0' },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        {/* Main Content Area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            pb: { xs: 10, md: 4 },
            width: { md: `calc(100% - ${drawerWidth}px)` },
            bgcolor: '#f8faf9',
            minHeight: '100vh',
            mt: { xs: 7, md: 0 }
          }}
        >
          <Routes>
            <Route path="dashboard" element={<RPTDashboard />} />
            <Route path="" element={<RPTDashboard />} />
            <Route path="properties" element={<Properties />} />
            <Route path="owners" element={<PropertyOwners />} />
            <Route path="assessments" element={<AssessmentsPage />} />
            <Route path="payments" element={<Payments />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="custom-reports" element={<CustomReportsPage />} />
            <Route path="calculator" element={<TaxCalculator />} />
            <Route path="settings" element={<SettingsPage />} />
          </Routes>
        </Box>

        {/* Sticky Mobile Bottom Navigation */}
        <MobileBottomBar
          items={menuItems}
          currentPath={location.pathname}
          onNavigate={(path) => navigate(path)}
          onToggleMenu={handleDrawerToggle}
          isMenuOpen={mobileOpen}
        />

        {/* Modals */}
        <ProfileModal
          user={user}
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          onUpdate={handleProfileUpdate}
        />
        <SettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
        />
      </Box>
    </SystemThemeProvider>
  );
};

export default RPTSystem;
